import { authFetch, buildApiUrl } from '@/config/api';
import type { 
  NotificationSummary, 
  UnreadMention, 
  FocusWarning,
  EmergencyOverride 
} from '@/types/mentions';

/**
 * Simplified Mention Service
 * 
 * Handles all @mention-related API calls:
 * - Getting notification summary for the bell
 * - Getting list of unread mentions
 * - Marking mentions as seen
 * - Getting focus warnings before @mentioning
 * - Recording emergency overrides
 */
export const mentionService = {
  /**
   * Get notification summary for the bell indicator.
   */
  async getSummary(): Promise<NotificationSummary> {
    const response = await authFetch(buildApiUrl('/api/mentions/summary'));
    if (!response.ok) {
      console.error('Failed to fetch notification summary');
      return { unreadCount: 0, hasEmergency: false, channelCount: 0 };
    }
    return response.json();
  },

  /**
   * Get list of unread mentions for the notification panel.
   */
  async getUnreadMentions(limit = 50): Promise<UnreadMention[]> {
    const response = await authFetch(buildApiUrl(`/api/mentions?limit=${limit}`));
    if (!response.ok) {
      console.error('Failed to fetch unread mentions');
      return [];
    }
    return response.json();
  },

  /**
   * Mark all mentions as seen.
   */
  async markAllSeen(): Promise<void> {
    const response = await authFetch(buildApiUrl('/api/mentions/mark-seen'), {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error('Failed to mark mentions as seen');
    }
  },

  /**
   * Mark specific mentions as seen.
   */
  async markSeen(mentionIds: string[]): Promise<void> {
    const response = await authFetch(buildApiUrl('/api/mentions/mark-items-seen'), {
      method: 'POST',
      body: JSON.stringify({ mentionIds }),
    });
    if (!response.ok) {
      throw new Error('Failed to mark mentions as seen');
    }
  },

  /**
   * Mark all mentions in a channel as seen.
   */
  async markChannelSeen(channelId: string): Promise<void> {
    const response = await authFetch(buildApiUrl(`/api/mentions/mark-channel-seen/${channelId}`), {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error('Failed to mark channel mentions as seen');
    }
  },

  /**
   * Get focus warning for a username before @mentioning.
   */
  async getFocusWarning(username: string): Promise<FocusWarning> {
    const response = await authFetch(buildApiUrl(`/api/mentions/focus-warning/${encodeURIComponent(username)}`));
    if (!response.ok) {
      return { userFound: false } as FocusWarning;
    }
    return response.json();
  },

  /**
   * Get focus warning by user ID.
   */
  async getFocusWarningById(userId: string): Promise<FocusWarning> {
    const response = await authFetch(buildApiUrl(`/api/mentions/focus-warning/by-id/${userId}`));
    if (!response.ok) {
      return { userFound: false } as FocusWarning;
    }
    return response.json();
  },

  /**
   * Record an emergency override when notifying a focused user.
   */
  async recordEmergencyOverride(
    recipientUserId: string, 
    messageId?: string, 
    reason?: string
  ): Promise<{ overrideId: string }> {
    const response = await authFetch(buildApiUrl('/api/mentions/emergency-override'), {
      method: 'POST',
      body: JSON.stringify({ recipientUserId, messageId, reason }),
    });
    if (!response.ok) {
      throw new Error('Failed to record emergency override');
    }
    return response.json();
  },

  /**
   * Get emergency override logs (admin only).
   */
  async getEmergencyOverrides(limit = 50, since?: Date): Promise<EmergencyOverride[]> {
    let url = `/api/mentions/emergency-overrides?limit=${limit}`;
    if (since) {
      url += `&since=${since.toISOString()}`;
    }
    const response = await authFetch(buildApiUrl(url));
    if (!response.ok) {
      console.error('Failed to fetch emergency override logs');
      return [];
    }
    return response.json();
  },
};

export default mentionService;
